import { createPortal } from 'react-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, fetchPulse } from '@/lib/api';
import type { PulseOpening, PulseResponse } from '@/types/pulse';

// ─── Helpers ──────────────────────────────────────────────────────────────

function fmtMonth(ym: string): string {
  const [y, m] = ym.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString('default', {
    month: 'short',
    year: 'numeric',
  });
}

function trendLabel(t: PulseOpening['trend']): string {
  if (t === 'rising') return 'Rising';
  if (t === 'falling') return 'Falling';
  return 'Stable';
}

type SortKey = 'popular' | 'rising' | 'winrate';

function sortOpenings(openings: PulseOpening[], key: SortKey): PulseOpening[] {
  const copy = [...openings];
  if (key === 'popular') return copy.sort((a, b) => b.total_games - a.total_games);
  if (key === 'rising') {
    const order: Record<string, number> = { rising: 0, stable: 1, falling: 2 };
    return copy.sort((a, b) => (order[a.trend] - order[b.trend]) || b.total_games - a.total_games);
  }
  return copy.sort((a, b) => b.win_rate - a.win_rate);
}

const TIME_CLASSES = [
  { id: 'all',    label: 'All' },
  { id: 'blitz',  label: 'Blitz' },
  { id: 'rapid',  label: 'Rapid' },
  { id: 'bullet', label: 'Bullet' },
];

// ─── Chess board (FEN renderer, no new packages) ───────────────────────────

const PIECE_UNICODE: Record<string, string> = {
  K: '♔', Q: '♕', R: '♖', B: '♗', N: '♘', P: '♙',
  k: '♚', q: '♛', r: '♜', b: '♝', n: '♞', p: '♟',
};

function parseFen(fen: string): (string | null)[][] {
  const board: (string | null)[][] = [];
  for (const rank of fen.split(' ')[0].split('/')) {
    const row: (string | null)[] = [];
    for (const ch of rank) {
      const n = parseInt(ch, 10);
      if (!isNaN(n)) for (let i = 0; i < n; i++) row.push(null);
      else row.push(ch);
    }
    board.push(row);
  }
  return board;
}

function MiniBoard({ fen, size = 240 }: { fen: string; size?: number }) {
  const board = parseFen(fen);
  const sq = Math.floor(size / 8);
  return (
    <div
      className="pulse-board"
      style={{ width: size, height: size, gridTemplateColumns: `repeat(8, ${sq}px)` }}
      aria-hidden
    >
      {board.map((row, ri) =>
        row.map((piece, ci) => {
          const light = (ri + ci) % 2 === 0;
          const isWhite = piece !== null && piece === piece.toUpperCase();
          return (
            <div key={`${ri}-${ci}`} className={`pulse-sq${light ? ' pulse-sq--light' : ''}`} style={{ width: sq, height: sq }}>
              {piece && (
                <span
                  className={`pulse-piece${isWhite ? ' pulse-piece--white' : ' pulse-piece--black'}`}
                  style={{ fontSize: sq * 0.70 }}
                >
                  {PIECE_UNICODE[piece] ?? ''}
                </span>
              )}
            </div>
          );
        }),
      )}
    </div>
  );
}

// ─── FEN cache + fetch ────────────────────────────────────────────────────

const fenCache = new Map<string, string | null>();
const fenInflight = new Map<string, Promise<string | null>>();

function loadFen(name: string): Promise<string | null> {
  if (fenCache.has(name)) return Promise.resolve(fenCache.get(name) ?? null);
  if (fenInflight.has(name)) return fenInflight.get(name)!;
  const p = fetch(`/api/pulse/opening-fen?${new URLSearchParams({ name })}`)
    .then((r) => (r.ok ? (r.json() as Promise<{ fen: string | null }>) : null))
    .then((d) => d?.fen ?? null)
    .catch(() => null)
    .then((fen) => {
      fenCache.set(name, fen);
      fenInflight.delete(name);
      return fen;
    });
  fenInflight.set(name, p);
  return p;
}

function useFen(name: string): string | null {
  const [fen, setFen] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    loadFen(name).then((f) => { if (live) setFen(f); });
    return () => { live = false; };
  }, [name]);
  return fen;
}

// ─── Hover tooltip (portal) ───────────────────────────────────────────────

interface TooltipState {
  opening: PulseOpening;
  anchorRect: DOMRect;
}

const TOOLTIP_W = 224;
const TOOLTIP_H = 268;

function BoardTooltip({ state }: { state: TooltipState }) {
  const fen = useFen(state.opening.opening);
  const r = state.anchorRect;

  let left = r.right + 14;
  if (left + TOOLTIP_W > window.innerWidth - 8) left = r.left - TOOLTIP_W - 14;
  const top = Math.max(8, Math.min(r.top, window.innerHeight - TOOLTIP_H - 8));

  return createPortal(
    <div className="pulse-tooltip" style={{ left, top }} aria-hidden>
      <span className="pulse-tooltip-name">{state.opening.opening}</span>
      {fen ? (
        <MiniBoard fen={fen} size={200} />
      ) : (
        <div className="pulse-tooltip-loading">
          <span aria-hidden>♛</span> Loading position...
        </div>
      )}
      <div className="pulse-tooltip-foot">
        <span>{state.opening.total_games} games</span>
        <span>{Math.round(state.opening.win_rate * 100)}% wins</span>
      </div>
    </div>,
    document.body,
  );
}

// ─── Details modal (portal) ───────────────────────────────────────────────

function OpeningModal({
  opening, months, onClose,
}: {
  opening: PulseOpening; months: string[]; onClose: () => void;
}) {
  const fen = useFen(opening.opening);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const chesscomUrl = opening.eco ? `https://www.chess.com/openings/${opening.eco}` : null;
  const lichessUrl  = fen
    ? `https://lichess.org/analysis/${encodeURIComponent(fen.split(' ').slice(0, 4).join('_'))}`
    : 'https://lichess.org/analysis';

  const maxGames = Math.max(...months.map((m) => opening.by_month[m]?.games ?? 0), 1);

  return createPortal(
    <div
      className="pulse-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={opening.opening}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="pulse-modal">
        <button type="button" className="pulse-modal-close" onClick={onClose} aria-label="Close">✕</button>

        {/* ── Left: board ── */}
        <div className="pulse-modal-left">
          <div className="pulse-modal-header">
            <span className={`pulse-trend pulse-trend--${opening.trend}`}>
              {opening.trend === 'rising' ? '↑' : opening.trend === 'falling' ? '↓' : '→'}{' '}
              {trendLabel(opening.trend)}
            </span>
            <h2 className="pulse-modal-name">{opening.opening}</h2>
            {opening.eco && <span className="pulse-card-eco">{opening.eco}</span>}
          </div>

          <div className="pulse-modal-board-wrap">
            {fen ? (
              <MiniBoard fen={fen} size={288} />
            ) : (
              <div className="pulse-modal-board-placeholder">
                <span className="pulse-modal-ph-piece" aria-hidden>♛</span>
                <span className="pulse-modal-ph-note">Loading starting position...</span>
              </div>
            )}
          </div>

          <div className="pulse-modal-links">
            {chesscomUrl && (
              <a href={chesscomUrl} target="_blank" rel="noopener noreferrer" className="pulse-modal-link">
                Chess.com opening →
              </a>
            )}
            <a href={lichessUrl} target="_blank" rel="noopener noreferrer" className="pulse-modal-link pulse-modal-link--ghost">
              Analyze on Lichess →
            </a>
          </div>
        </div>

        {/* ── Right: stats ── */}
        <div className="pulse-modal-right">
          <div className="pulse-modal-stats-row">
            <div className="pulse-modal-stat-cell">
              <span className="pulse-card-stat-val">{opening.total_games}</span>
              <span className="pulse-card-stat-key">Total games</span>
            </div>
            <div className="pulse-modal-stat-cell">
              <span className="pulse-card-stat-val">{Math.round(opening.win_rate * 100)}%</span>
              <span className="pulse-card-stat-key">Win rate</span>
            </div>
          </div>

          <div className="pulse-modal-section-label">Month by month</div>
          <div className="pulse-modal-month-list">
            {months.map((m) => {
              const d = opening.by_month[m];
              const games = d?.games ?? 0;
              const pct = Math.round((games / maxGames) * 100);
              const wr = d ? Math.round(d.win_rate * 100) : 0;
              return (
                <div key={m} className="pulse-modal-month-row">
                  <span className="pulse-modal-month-label">{fmtMonth(m)}</span>
                  <div className="pulse-modal-month-track">
                    <div
                      className={`pulse-bar-fill pulse-bar-fill--${opening.trend}`}
                      style={{ width: `${pct}%`, height: '100%', borderRadius: 4 }}
                    />
                  </div>
                  <span className="pulse-modal-month-num">{games}</span>
                  <span className={`pulse-modal-month-wr pulse-modal-wr--${opening.trend}`}>{wr}%</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Opening card ─────────────────────────────────────────────────────────

function OpeningCard({
  opening, months, onHover, onHoverEnd, onOpen,
}: {
  opening: PulseOpening;
  months: string[];
  onHover: (o: PulseOpening, rect: DOMRect) => void;
  onHoverEnd: () => void;
  onOpen: (o: PulseOpening) => void;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const maxGames = Math.max(...months.map((m) => opening.by_month[m]?.games ?? 0), 1);

  return (
    <div
      ref={cardRef}
      className="pulse-card pulse-card--interactive"
      role="button"
      tabIndex={0}
      aria-label={`${opening.opening}, ${opening.total_games} games, ${trendLabel(opening.trend)}. Press Enter to see details.`}
      onMouseEnter={() => cardRef.current && onHover(opening, cardRef.current.getBoundingClientRect())}
      onMouseLeave={onHoverEnd}
      onClick={() => onOpen(opening)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(opening); } }}
    >
      <div className="pulse-card-top">
        <div className="pulse-card-name-row">
          <span className="pulse-card-name">{opening.opening}</span>
          {opening.eco && <span className="pulse-card-eco">{opening.eco}</span>}
        </div>
        <span className={`pulse-trend pulse-trend--${opening.trend}`}>
          {opening.trend === 'rising' ? '↑' : opening.trend === 'falling' ? '↓' : '→'}{' '}
          {trendLabel(opening.trend)}
        </span>
      </div>

      <div className="pulse-bars">
        {months.map((m) => {
          const d = opening.by_month[m];
          const games = d?.games ?? 0;
          const pct = Math.round((games / maxGames) * 100);
          return (
            <div key={m} className="pulse-bar-col">
              <div className="pulse-bar-track">
                <div
                  className={`pulse-bar-fill pulse-bar-fill--${opening.trend}`}
                  style={{ height: `${pct}%` }}
                  role="presentation"
                />
              </div>
              <span className="pulse-bar-count">{games > 0 ? games : ''}</span>
              <span className="pulse-bar-label">{fmtMonth(m)}</span>
            </div>
          );
        })}
      </div>

      <div className="pulse-card-footer">
        <span className="pulse-card-stat">
          <span className="pulse-card-stat-val">{opening.total_games}</span>
          <span className="pulse-card-stat-key">games</span>
        </span>
        <span className="pulse-card-stat">
          <span className="pulse-card-stat-val">{Math.round(opening.win_rate * 100)}%</span>
          <span className="pulse-card-stat-key">win rate</span>
        </span>
        <span className="pulse-card-hint" aria-hidden>tap for board →</span>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────

type Status = 'idle' | 'loading' | 'success' | 'error';

export default function PulsePage() {
  const [timeClass, setTimeClass] = useState<string>('all');
  const [sort, setSort]           = useState<SortKey>('popular');
  const [status, setStatus]       = useState<Status>('idle');
  const [data, setData]           = useState<PulseResponse | null>(null);
  const [errorMsg, setErrorMsg]   = useState('');

  // Tooltip state (desktop hover)
  const [tooltip, setTooltip]         = useState<TooltipState | null>(null);
  const hoverTimerRef                 = useRef<number | null>(null);

  // Modal state (click / tap)
  const [modalOpening, setModalOpening] = useState<PulseOpening | null>(null);

  const load = useCallback(async (tc: string) => {
    setStatus('loading');
    setData(null);
    setErrorMsg('');
    setTooltip(null);
    setModalOpening(null);
    try {
      const result = await fetchPulse(tc === 'all' ? undefined : tc, 3);
      setData(result);
      setStatus('success');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Could not load trend data.';
      setErrorMsg(msg);
      setStatus('error');
    }
  }, []);

  useEffect(() => { void load(timeClass); }, [timeClass, load]);

  const handleHover = useCallback((opening: PulseOpening, anchorRect: DOMRect) => {
    if (hoverTimerRef.current) window.clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = window.setTimeout(
      () => setTooltip({ opening, anchorRect }),
      220,
    );
  }, []);

  const handleHoverEnd = useCallback(() => {
    if (hoverTimerRef.current) window.clearTimeout(hoverTimerRef.current);
    setTooltip(null);
  }, []);

  const displayed = data ? sortOpenings(data.openings, sort) : [];

  return (
    <div className="pulse-page">

      <header className="pulse-header">
        <span className="pulse-eyebrow">Opening trends</span>
        <h1 className="pulse-title">Pulse</h1>
        <p className="pulse-sub">
          Which openings are being played more this month? Which are fading?
          Data from tracked players, updated nightly via dbt. Hover a card to preview the board.
          Click for full details.
        </p>
        {data && (
          <p className="pulse-meta-note">
            {data.tracked_users} tracked player{data.tracked_users !== 1 ? 's' : ''} in this dataset.
            Not Chess.com-wide.
          </p>
        )}
      </header>

      <div className="pulse-controls">
        <div className="pulse-tabs" role="group" aria-label="Time class filter">
          {TIME_CLASSES.map((tc) => (
            <button
              key={tc.id}
              type="button"
              className={`pulse-tab${timeClass === tc.id ? ' pulse-tab--active' : ''}`}
              onClick={() => setTimeClass(tc.id)}
              aria-pressed={timeClass === tc.id}
            >
              {tc.label}
            </button>
          ))}
        </div>

        <div className="pulse-sort" role="group" aria-label="Sort openings">
          <span className="pulse-sort-label">Sort by</span>
          {(['popular', 'rising', 'winrate'] as SortKey[]).map((k) => (
            <button
              key={k}
              type="button"
              className={`pulse-sort-btn${sort === k ? ' pulse-sort-btn--active' : ''}`}
              onClick={() => setSort(k)}
              aria-pressed={sort === k}
            >
              {k === 'popular' ? 'Most played' : k === 'rising' ? 'Rising' : 'Win rate'}
            </button>
          ))}
        </div>
      </div>

      {status === 'loading' && (
        <div className="pulse-loading">
          <span className="pulse-loading-dot" />
          <span className="pulse-loading-dot" />
          <span className="pulse-loading-dot" />
          <span>Querying BigQuery</span>
        </div>
      )}

      {status === 'error' && (
        <div className="pulse-error" role="alert">
          <p>{errorMsg}</p>
          <button type="button" className="pulse-retry-btn" onClick={() => void load(timeClass)}>
            Try again
          </button>
        </div>
      )}

      {status === 'success' && data && (
        <>
          {data.months.length >= 2 && (
            <div className="pulse-month-strip">
              {data.months.map((m) => (
                <div key={m} className="pulse-month-chip">
                  <span className="pulse-month-name">{fmtMonth(m)}</span>
                  <span className="pulse-month-total">
                    {(data.total_by_month[m] ?? 0).toLocaleString()} games
                  </span>
                </div>
              ))}
            </div>
          )}

          {displayed.length === 0 ? (
            <p className="pulse-empty">
              No opening data for this filter. Try "All" or check back after the next nightly run.
            </p>
          ) : (
            <div className="pulse-grid">
              {displayed.map((op) => (
                <OpeningCard
                  key={op.opening}
                  opening={op}
                  months={data.months}
                  onHover={handleHover}
                  onHoverEnd={handleHoverEnd}
                  onOpen={setModalOpening}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Desktop hover tooltip */}
      {tooltip && <BoardTooltip state={tooltip} />}

      {/* Click / tap modal */}
      {modalOpening && data && (
        <OpeningModal
          opening={modalOpening}
          months={data.months}
          onClose={() => setModalOpening(null)}
        />
      )}
    </div>
  );
}
